import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Money, CurrencyCircleDollar, Bank, Warning, FileXls, ChartLineUp } from "@phosphor-icons/react";

const now = new Date();
const MONTHS_ID = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];

export default function Reports() {
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [busy, setBusy] = useState(false);

  const monthOptions = Array.from({ length: 24 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return { key, label: `${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}` };
  });

  const download = async (path, name, useMonth = true) => {
    setBusy(true);
    try {
      const qs = useMonth ? `?month=${month}` : "";
      const r = await api.get(`${path}${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${name}-${useMonth ? month : "current"}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel diunduh");
    } catch { toast.error("Gagal mengunduh"); }
    finally { setBusy(false); }
  };

  const cards = [
    { to: "/cashbook", icon: Bank, title: "Buku Kas Harian", desc: "Arus kas per hari — pemasukan vs pengeluaran + saldo bersih", color: "#10B981" },
    { to: "/commission", icon: CurrencyCircleDollar, title: "Komisi Makelar", desc: "Rekap komisi per makelar dari penjualan & pembelian", color: "#F97316" },
    { to: "/overdue", icon: Warning, title: "Piutang Telat", desc: "Rekening kredit yang menunggak per kategori", color: "#E63946" },
    { to: "/cars?tab=sold", icon: ChartLineUp, title: "Mobil Sudah Laku", desc: "Detail penjualan + margin per unit per bulan", color: "#2563EB" },
  ];

  return (
    <div>
      <PageHeader title="Laporan" subtitle="Pusat laporan keuangan & operasional showroom" />
      <div className="p-4 md:p-6 lg:p-10">
        {/* Excel export panel */}
        <div className="border border-gray-200 bg-white p-6 mb-8">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-4 flex items-center gap-2">
            <FileXls size={14} weight="bold" /> Download Laporan Excel
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month" className="h-11 px-3 border border-gray-300 bg-white focus:outline-none focus:border-[#0A0A0A]">
              {monthOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <button
              onClick={() => download("/reports/monthly-sales.xlsx", "rekap-penjualan")}
              disabled={busy}
              data-testid="dl-monthly-sales"
              className="h-11 px-4 bg-[#10B981] hover:bg-[#0d966b] text-white uppercase tracking-wider text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <FileXls size={14} weight="bold" /> Rekap Penjualan
            </button>
            <button
              onClick={() => download("/reports/daily-cashbook.xlsx", "buku-kas")}
              disabled={busy}
              data-testid="dl-cashbook"
              className="h-11 px-4 bg-[#10B981] hover:bg-[#0d966b] text-white uppercase tracking-wider text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <FileXls size={14} weight="bold" /> Buku Kas
            </button>
            <button
              onClick={() => download("/reports/piutang.xlsx", "piutang", false)}
              disabled={busy}
              data-testid="dl-piutang"
              className="h-11 px-4 bg-[#10B981] hover:bg-[#0d966b] text-white uppercase tracking-wider text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <FileXls size={14} weight="bold" /> Piutang
            </button>
          </div>
        </div>

        {/* Report shortcuts */}
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-4">Lihat Detail Laporan</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cards.map((c) => (
            <Link key={c.to} to={c.to} data-testid={`report-card-${c.to.replace(/\W+/g, "-")}`} className="border border-gray-200 p-6 bg-white hover:border-gray-900 transition-colors group">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center" style={{ background: c.color + "20", color: c.color }}>
                  <c.icon size={22} weight="bold" />
                </div>
                <div>
                  <div className="font-display font-bold text-lg group-hover:underline">{c.title}</div>
                  <div className="text-sm text-gray-600 mt-1">{c.desc}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
